import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, take, takeUntil, Subject, forkJoin, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

import { IAumyeState } from 'src/app/state-management/states/aumye.state';
import * as fromDashboardActions from 'src/app/state-management/actions/dashboard.action';

import { UserRegDetails } from 'src/app/models/user';
import {
  EncryptionService,
  UserDetailsToken,
} from 'src/app/services/Encryption/encryption.service';
import { environment } from 'src/app/environments/environments';
import { ToastrService } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { selectOrgUsersData } from 'src/app/state-management/selectors/dashboard.selectors';

interface LocationCoordinates {
  lat: number;
  lng: number;
}

export interface RaiseIncidentOptions {
  incidentName?: string;
  incidentDescription?: string;
  incidentLocation?: { lat: number; lng: number };
  incidentLocationAddress?: string;
  priority?: 'high' | 'medium' | 'low';
  reporter?: string; // userId
  assignees?: string[]; // array of userIds
  evidenceUrl?: string;
  source?: string;
  tagName?: string;
  tagEmail?: string;
  tagNumber?: string;
  tagType?: string;
  additionalData?: any;
  skipDefaultAssignee?: boolean; // Flag to skip default Aumye assignee
}

export interface IncidentResponse {
  success: boolean;
  data?: any;
  error?: any;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class IncidentReportingService {
  public orgUsers$: Observable<UserRegDetails[]>;
  private currentUser: UserDetailsToken;
  private destroy$ = new Subject<void>();
  private readonly DEFAULT_ASSIGNEE_NAME = 'Aumye';
  private readonly apiKey = environment.googleMapsApiKey;
  constructor(
    private store: Store<IAumyeState>,
    private actions$: Actions,
    private encryptionService: EncryptionService,
    private toastr: ToastrService,
    private http: HttpClient
  ) {
    this.currentUser = this.encryptionService.getCurrentUser();
    this.orgUsers$ = this.store.pipe(select(selectOrgUsersData));
  }

  /**
   * Main method to raise an incident with optional parameters
   * @param options - Optional incident parameters
   * @param showMessages - Whether to show success/error messages (default: true)
   * @returns Observable with incident creation result
   */
  raiseIncident(
    options: RaiseIncidentOptions = {},
    showMessages: boolean = true
  ): Observable<IncidentResponse> {
    return new Observable((observer) => {
      // Validate required fields first
      const validation = this.validateIncidentOptions(options);
      if (!validation.isValid) {
        const errorResponse: IncidentResponse = {
          success: false,
          error: validation.error,
          message: validation.error,
        };

        if (showMessages) {
          this.toastr.error(validation.error);
        }

        observer.next(errorResponse);
        observer.complete();
        return;
      }

      // Get current location if not provided
      if (!options.incidentLocation) {
        this.getCurrentLocation().subscribe({
          next: (location) => {
            options.incidentLocation = location;
            this.processIncidentCreation(options, showMessages, observer);
          },
          error: (error) => {
            console.error('Error getting location:', error);

            const errorResponse: IncidentResponse = {
              success: false,
              error:
                'Unable to get location. Location is required to create an incident.',
              message: 'Location is required',
            };

            if (showMessages) {
              this.toastr.error(
                'Unable to get location. Please provide location manually.'
              );
            }

            observer.next(errorResponse);
            observer.complete();
          },
        });
      } else {
        this.processIncidentCreation(options, showMessages, observer);
      }
    });
  }

  /**
   * Validate incident options before processing
   */
  private validateIncidentOptions(options: RaiseIncidentOptions): {
    isValid: boolean;
    error?: string;
  } {
    // Check if location will be available
    const hasLocation = options.incidentLocation || 'geolocation' in navigator;
    if (!hasLocation) {
      return {
        isValid: false,
        error:
          'Location is required. Geolocation is not supported on this device.',
      };
    }

    // Note: We don't check assignees here because it will be handled in processIncidentCreation
    // where we try to find the default "Aumye" user

    return { isValid: true };
  }
  /**
   * Process the incident creation with all data
   */
  private processIncidentCreation(
    options: RaiseIncidentOptions,
    showMessages: boolean,
    observer: any
  ): void {
    // Get user details if needed
    this.orgUsers$.pipe(take(1)).subscribe({
      next: (users) => {
        // Find default assignee if no assignees provided
        if (!options.assignees || options.assignees.length === 0) {
          if (!options.skipDefaultAssignee) {
            const defaultAssignee = this.findDefaultAssignee(users);
            if (defaultAssignee) {
              options.assignees = [defaultAssignee.userId];
              console.log(
                `Assigned default user: ${defaultAssignee.firstname} ${defaultAssignee.lastname}`
              );
            } else {
              // No assignee found and no default assignee available
              const errorResponse: IncidentResponse = {
                success: false,
                error:
                  'No assignee available. Default assignee "Aumye" not found in organization.',
                message: 'Assignee is required',
              };

              if (showMessages) {
                this.toastr.error(
                  'Unable to create incident. No assignee available.'
                );
              }

              console.warn(
                'Default assignee "Aumye" not found in organization users'
              );
              observer.next(errorResponse);
              observer.complete();
              return;
            }
          } else {
            // Skip default assignee but still no assignees provided
            const errorResponse: IncidentResponse = {
              success: false,
              error: 'At least one assignee is required to create an incident.',
              message: 'Assignee is required',
            };

            if (showMessages) {
              this.toastr.error('At least one assignee is required.');
            }

            observer.next(errorResponse);
            observer.complete();
            return;
          }
        }

        // Validate location is present
        if (!options.incidentLocation) {
          const errorResponse: IncidentResponse = {
            success: false,
            error: 'Location is required to create an incident.',
            message: 'Location is required',
          };

          if (showMessages) {
            this.toastr.error('Location is required to create an incident.');
          }

          observer.next(errorResponse);
          observer.complete();
          return;
        }

        const incidentData = this.buildIncidentPayload(options, users);
        this.dispatchIncident(incidentData, showMessages, observer);
      },
      error: (error) => {
        observer.next({
          success: false,
          error: error,
          message: 'Failed to get user data',
        });
        observer.complete();
      },
    });
  }

  /**
   * Find default assignee user by name "Aumye"
   */
  private findDefaultAssignee(users: UserRegDetails[]): UserRegDetails | null {
    // Search for user with name containing "Aumye" (case-insensitive)
    const defaultUser = users.find(
      (user) =>
        user.firstname
          ?.toLowerCase()
          .includes(this.DEFAULT_ASSIGNEE_NAME.toLowerCase()) ||
        user.lastname
          ?.toLowerCase()
          .includes(this.DEFAULT_ASSIGNEE_NAME.toLowerCase()) ||
        `${user.firstname} ${user.lastname}`
          .toLowerCase()
          .includes(this.DEFAULT_ASSIGNEE_NAME.toLowerCase())
    );

    return defaultUser || null;
  }

  /**
   * Get default assignee user
   */
  getDefaultAssignee(): Observable<UserRegDetails | null> {
    return this.orgUsers$.pipe(
      take(1),
      map((users) => this.findDefaultAssignee(users))
    );
  }

  /**
   * Build the incident payload with defaults
   */
  private buildIncidentPayload(
    options: RaiseIncidentOptions,
    users: UserRegDetails[]
  ): any {
    // Get reporter details
    const reporterId = options.reporter || this.currentUser.userID;
    const reporter = users.find((u) => u.userId === reporterId);
    const reporterName = reporter
      ? `${reporter.firstname} ${reporter.lastname}`
      : 'Unknown';

    // Get assignees details
    const assigneeIds = options.assignees || [];
    const selectedUserDetails = users
      .filter((user) => assigneeIds.includes(user.userId))
      .map((user) => ({
        HostName: `${user.firstname} ${user.lastname}`,
        HostUserId: user.userId,
        HostEmail: user.email,
        HostPhone: user.phoneNumber,
      }));

    // Build location data
    let locationString = '';
    let locationAddress = options.incidentLocationAddress || '';

    if (options.incidentLocation) {
      locationString = JSON.stringify([options.incidentLocation]);

      // Get address if not provided
      if (!locationAddress) {
        this.getLocationAddress(options.incidentLocation)
          .pipe(take(1))
          .subscribe((response: any) => {
            if (response?.results?.length > 0) {
              locationAddress = response.results[0].formatted_address;
            }
          });
      }
    }

    // Build tag information
    const tagData: any = {};
    if (options.tagName) {
      tagData.eventOwnerName = options.tagName;
      tagData.eventOwnerEmail = options.tagEmail || '';
      tagData.eventOwnerPhone = options.tagNumber || '';
      tagData.eventOwnerType = options.tagType || 'New';
    }

    // Build final payload
    const payload = {
      incidentType: 'manual',
      incidentName: options.incidentName || 'Untitled Incident',
      incidentDescription:
        options.incidentDescription || 'No description provided',
      incidentLocation: locationString,
      incidentLocationAddress: locationAddress,
      source: options.source || '',
      incidentStatus: 0, // Open
      incidentCriticality: options.priority || 'medium',
      priority: options.priority || 'medium',
      isLocationAdded: !!options.incidentLocation,
      eventEvidence: options.evidenceUrl || null,
      createdAt: new Date(),
      eventCreatedBy: reporterName,
      eventCreatedByUserID: reporterId,
      reporter: reporterId,
      assignees: assigneeIds,
      selectedUsers: selectedUserDetails,
      ...tagData,
      ...(options.additionalData || {}),
    };

    return payload;
  }
  public getLocationAddress(coords: LocationCoordinates): Observable<any> {
    const { lat, lng } = coords;
    const url =
      `https://maps.googleapis.com/maps/api/geocode/json` +
      `?latlng=${lat},${lng}` +
      `&key=${this.apiKey}`;
    return this.http.get<any>(url);
  }
  /**
   * Dispatch the incident creation action
   */
  private dispatchIncident(
    incidentData: any,
    showMessages: boolean,
    observer: any
  ): void {
    console.log(incidentData, 'incidentData');
    this.store.dispatch(
      new fromDashboardActions.CreateManualIncident(incidentData)
    );

    // Success handler
    this.actions$
      .pipe(
        ofType(
          fromDashboardActions.DashboardActionTypes
            .CREATE_MANUAL_INCIDENT_SUCCESS
        ),
        take(1)
      )
      .subscribe((action: any) => {
        if (showMessages) {
          this.toastr.success('Incident created successfully!');
        }
        observer.next({
          success: true,
          data: action.payload,
          message: 'Incident created successfully!',
        });
        observer.complete();
      });

    // Failure handler
    this.actions$
      .pipe(
        ofType(
          fromDashboardActions.DashboardActionTypes
            .CREATE_MANUAL_INCIDENT_FAILURE
        ),
        take(1)
      )
      .subscribe((action: any) => {
        if (showMessages) {
          this.toastr.error('Failed to create incident');
        }
        observer.next({
          success: false,
          error: action.payload,
          message: 'Failed to create incident',
        });
        observer.complete();
      });
  }

  /**
   * Get current geolocation
   */
  private getCurrentLocation(): Observable<{ lat: number; lng: number }> {
    return new Observable((observer) => {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            observer.next({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
            observer.complete();
          },
          (error) => {
            observer.error(error);
          }
        );
      } else {
        observer.error(new Error('Geolocation not supported'));
      }
    });
  }

  /**
   * Quick raise incident with minimal parameters
   * Automatically assigns to default "Aumye" user if no assignees provided
   */
  quickRaiseIncident(
    incidentName: string,
    description: string,
    priority: 'high' | 'medium' | 'low' = 'medium'
  ): Observable<IncidentResponse> {
    return this.raiseIncident({
      incidentName,
      incidentDescription: description,
      priority,
    });
  }

  /**
   * Raise incident with assignees
   */
  raiseIncidentWithAssignees(
    incidentName: string,
    description: string,
    assigneeUserIds: string[],
    priority: 'high' | 'medium' | 'low' = 'medium'
  ): Observable<IncidentResponse> {
    return this.raiseIncident({
      incidentName,
      incidentDescription: description,
      assignees: assigneeUserIds,
      priority,
    });
  }

  /**
   * Raise incident with location
   * Automatically assigns to default "Aumye" user if no assignees provided
   */
  raiseIncidentWithLocation(
    incidentName: string,
    description: string,
    location: { lat: number; lng: number },
    address?: string
  ): Observable<IncidentResponse> {
    return this.raiseIncident({
      incidentName,
      incidentDescription: description,
      incidentLocation: location,
      incidentLocationAddress: address,
    });
  }

  /**
   * Raise incident with evidence
   * Automatically assigns to default "Aumye" user if no assignees provided
   */
  raiseIncidentWithEvidence(
    incidentName: string,
    description: string,
    evidenceUrl: string,
    priority: 'high' | 'medium' | 'low' = 'high'
  ): Observable<IncidentResponse> {
    return this.raiseIncident({
      incidentName,
      incidentDescription: description,
      evidenceUrl,
      priority,
    });
  }

  /**
   * Raise incident with tag user
   * Automatically assigns to default "Aumye" user if no assignees provided
   */
  raiseIncidentWithTag(
    incidentName: string,
    description: string,
    tagName: string,
    tagEmail: string,
    tagNumber: string,
    tagType: string = 'New'
  ): Observable<IncidentResponse> {
    return this.raiseIncident({
      incidentName,
      incidentDescription: description,
      tagName,
      tagEmail,
      tagNumber,
      tagType,
    });
  }

  /**
   * Raise incident without default assignee
   * Use this when you explicitly don't want any assignee
   */
  raiseIncidentWithoutAssignee(
    incidentName: string,
    description: string,
    priority: 'high' | 'medium' | 'low' = 'medium'
  ): Observable<IncidentResponse> {
    return this.raiseIncident({
      incidentName,
      incidentDescription: description,
      priority,
      skipDefaultAssignee: true,
    });
  }

  /**
   * Get current user
   */
  getCurrentUser(): UserDetailsToken {
    return this.currentUser;
  }

  /**
   * Get organization users
   */
  getOrgUsers(): Observable<UserRegDetails[]> {
    return this.orgUsers$;
  }

  /**
   * Check if default assignee exists
   */
  checkDefaultAssigneeExists(): Observable<boolean> {
    return this.getDefaultAssignee().pipe(map((user) => user !== null));
  }

  /**
   * Clean up
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
